import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { Toaster } from "@/components/ui/sonner";
import { detectBrand } from "@/components/Logo";

import Landing from "@/pages/Landing";
import PublicForm from "@/pages/PublicForm";
import Confirmation from "@/pages/Confirmation";
import CustomerCase from "@/pages/CustomerCase";
import InternalCase from "@/pages/InternalCase";
import Login from "@/pages/Login";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import NewCase from "@/pages/NewCase";
import CaseDetail from "@/pages/CaseDetail";
import Suppliers from "@/pages/Suppliers";
import Leaders from "@/pages/Leaders";
import HandlingGuide from "@/pages/HandlingGuide";
import AIInstructions from "@/pages/AIInstructions";
import MontorLookup from "@/pages/MontorLookup";
import MontorReport from "@/pages/MontorReport";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";

function Protected({ children, roles }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

// Reflects the current brand on <body data-brand="..."> whenever the route changes,
// so brand-scoped CSS variables (see index.css) apply to customer-facing pages.
function BrandBodyEffect() {
    const location = useLocation();
    useEffect(() => {
        const brand = detectBrand();
        if (brand === "gardinbussen") {
            document.body.removeAttribute("data-brand");
        } else {
            document.body.setAttribute("data-brand", brand);
        }
    }, [location.pathname]);
    return null;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <BrandBodyEffect />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/reklamation" element={<PublicForm />} />
                        {/* Gardinfabrikken brand paths — same components, brand auto-detected via pathname */}
                        <Route path="/gf" element={<Landing />} />
                        <Route path="/gf/reklamation" element={<PublicForm />} />
                        <Route path="/montor" element={<MontorLookup />} />
                        <Route path="/montor/ny" element={<MontorReport />} />
                        <Route path="/kvittering/:caseNumber" element={<Confirmation />} />
                        <Route path="/spor/:token" element={<CustomerCase />} />
                        <Route path="/intern/:token" element={<InternalCase />} />
                        <Route path="/login" element={<Login />} />

                        <Route
                            element={
                                <Protected>
                                    <DashboardLayout />
                                </Protected>
                            }
                        >
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/sag/ny" element={<NewCase />} />
                            <Route path="/sag/:id" element={<CaseDetail />} />
                            <Route path="/leverandorer" element={<Suppliers />} />
                            <Route
                                path="/brugere"
                                element={
                                    <Protected roles={["admin"]}>
                                        <Users />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/indstillinger"
                                element={
                                    <Protected roles={["admin"]}>
                                        <Settings />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/ledere"
                                element={
                                    <Protected roles={["admin", "kundeservice"]}>
                                        <Leaders />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/handleplan"
                                element={
                                    <Protected>
                                        <HandlingGuide />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/ai-instrukser"
                                element={
                                    <Protected>
                                        <AIInstructions />
                                    </Protected>
                                }
                            />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </div>
    );
}

export default App;
